import { create } from 'zustand';
import { api, type Demand, type DemandFilters, type DemandPriority, type DemandStatus } from '@/lib/api';
import type { RealtimeEventPayload } from '@/hooks/useSupabaseRealtime';

interface CreateDemandInput {
  title: string;
  description: string;
  priority?: DemandPriority;
  type?: string;
  project_id?: string;
  // Pipeline routing — opcional. Quando presente, demand ao virar 'executing'
  // dispatcha @pipeline-runner com este pipeline_slug. business_id obrigatorio
  // se pipeline.requires_business=true.
  pipeline_slug?: string;
  business_id?: string;
  // PR11 plano pipeline-canonical-rebuild: texto OBRIGATORIO descrevendo o
  // gatilho/motivo da demand. Form transforma em metadata.creator_context
  // canonical { origin: 'ui', creator_context: <texto> } antes do POST.
  creator_context: string;
}

interface DemandsStore {
  demands: Demand[];
  total: number;
  loading: boolean;
  filters: DemandFilters;

  /** Re-fetches the demands list from the backend. Resolves to `true` on
   *  success and `false` when the request failed (error is logged and the
   *  loading flag is cleared, but the previous list is preserved). The return
   *  value lets callers — e.g. the manual refresh button — surface a failure
   *  to the user without changing the swallow-and-keep-list behaviour that
   *  the realtime callers rely on. */
  fetchDemands: () => Promise<boolean>;
  /** PERF-02: aplica um evento realtime de `demands` DIRETO no estado (a row
   *  vem inteira no payload) — o card muda na hora, sem refetch de 200KB.
   *  Retorna false quando o payload não dá pra aplicar (caller refetcha). */
  applyRealtimeEvent: (payload?: RealtimeEventPayload) => boolean;
  setFilters: (filters: Partial<DemandFilters>) => void;
  transitionDemand: (id: string, newStatus: DemandStatus) => void;
  createDemand: (input: CreateDemandInput) => Promise<void>;
}

export const useDemandsStore = create<DemandsStore>((set, get) => ({
  demands: [],
  total: 0,
  // true inicial pra evitar flash de lista vazia antes do fetchDemands
  // mount completar (mesmo padrão do mc-store.tasksLoading).
  loading: true,
  filters: { per_page: 200 },

  fetchDemands: async () => {
    set({ loading: true });
    try {
      const result = await api.listDemands(get().filters);
      // Backend novo: { items, total, page, per_page }. Backend velho: { demands, ... }.
      // Array fallback pra clients legados que retornavam direto array.
      const demands = Array.isArray(result)
        ? result
        : (result.items ?? result.demands ?? []);
      const total = Array.isArray(result)
        ? demands.length
        : (result.total ?? demands.length);
      set({ demands, total, loading: false });
      return true;
    } catch (err) {
      console.error('Failed to fetch demands:', err);
      set({ loading: false });
      return false;
    }
  },

  applyRealtimeEvent: (payload) => {
    if (!payload?.eventType) return false;
    if (payload.eventType === 'DELETE') {
      const oldId = (payload.old as Partial<Demand> | null)?.id;
      if (!oldId) return false;
      set((state) => ({
        demands: state.demands.filter((d) => d.id !== oldId),
        total: Math.max(0, state.total - 1),
      }));
      return true;
    }
    const row = payload.new as Demand | null | undefined;
    if (!row?.id) return false;
    if (payload.eventType === 'INSERT') {
      set((state) =>
        state.demands.some((d) => d.id === row.id)
          ? state
          : { demands: [row, ...state.demands], total: state.total + 1 },
      );
      return true;
    }
    // UPDATE: merge na row existente; se a demand não está na lista (entrou
    // no escopo do filtro agora), sinaliza refetch.
    const known = get().demands.some((d) => d.id === row.id);
    if (!known) return false;
    set((state) => ({
      demands: state.demands.map((d) => (d.id === row.id ? { ...d, ...row } : d)),
    }));
    return true;
  },

  setFilters: (partial) => {
    set((state) => ({ filters: { ...state.filters, ...partial } }));
    get().fetchDemands();
  },

  transitionDemand: (id, newStatus) => {
    set((state) => ({
      demands: state.demands.map((d) =>
        d.id === id ? { ...d, status: newStatus } : d,
      ),
    }));

    api.transitionDemand(id, newStatus).catch((err) => {
      console.error('Failed to transition demand:', err);
      get().fetchDemands();
    });
  },

  createDemand: async (input) => {
    // PR11: forward direto (api.createDemand monta metadata.creator_context canonical).
    await api.createDemand(input);
    get().fetchDemands();
  },
}));
